import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { SalesManagement } from "./SalesManagement";
import { type SaleOpportunitySeed } from "./StandCrm";
import { DataQualityCenter } from "./DataQualityCenter";
import { AfterSalesCenter } from "./AfterSalesCenter";
import { CurrencyInput } from "./CurrencyInput";
import "./stand-finance.css";

type Vehicle = {
  id: string;
  stock_number: string;
  registration: string | null;
  make: string;
  model: string;
  currency_code: string;
};
type Summary = {
  vehicle_id: string;
  planned_expense_cents: number;
  realized_expense_cents: number;
  planned_income_cents: number;
  realized_income_cents: number;
  expected_margin_cents: number;
  realized_margin_cents: number;
  receivable_cents: number;
  currency_code: string;
};
type Entry = {
  id: string;
  vehicle_id: string;
  direction: string;
  category: string;
  state: string;
  amount_cents: number;
  currency_code: string;
  counterparty: string | null;
  branch_name: string | null;
  payment_method: string | null;
  effective_on: string | null;
  notes: string | null;
  created_at: string;
};
type EntryDocument = {
  id: string;
  entry_id: string;
  storage_path: string;
  original_name: string;
};
type Commission = {
  id: string;
  sale_id: string;
  amount_cents: number;
  currency_code: string;
  status: string;
  seller_user_id: string;
};
type Sale = {
  id: string;
  vehicle_id: string;
  customer_name: string;
  sale_price_cents: number;
  currency_code: string;
};

const categories: Record<string, string> = {
  acquisition: "Aquisição",
  transport: "Transporte",
  legalization: "Legalização",
  inspection: "Inspeção",
  workshop: "Oficina",
  parts: "Peças",
  preparation: "Preparação",
  cleaning: "Limpeza",
  photography: "Fotografia",
  advertising: "Publicidade",
  commission: "Comissão",
  warranty: "Garantia",
  tax: "Impostos",
  sale: "Venda",
  financing: "Financiamento",
  consignment: "Consignação",
  other: "Outro",
};
const states: Record<string, string> = {
  planned: "Previsto",
  committed: "Comprometido",
  invoiced: "Faturado",
  paid: "Pago",
  received: "Recebido",
};
const commissionStates: Record<string, string> = {
  expected: "Prevista",
  approved: "Aprovada",
  paid: "Paga",
};
const money = (value: number, currency: string) =>
  new Intl.NumberFormat(currency === "BRL" ? "pt-BR" : "pt-PT", {
    style: "currency",
    currency,
  }).format(value / 100);

export function StandFinance({
  organizationId,
  currencyCode,
  canManageSales,
  canManageFinance,
  canViewCosts,
  canViewMargin,
  canViewDocuments,
  canValidate,
  saleOpportunity,
  onSaleOpportunityConsumed,
}: {
  organizationId: string;
  currencyCode: string;
  canManageSales: boolean;
  canManageFinance: boolean;
  canViewCosts: boolean;
  canViewMargin: boolean;
  canViewDocuments: boolean;
  canValidate: boolean;
  saleOpportunity: SaleOpportunitySeed | null;
  onSaleOpportunityConsumed: () => void;
}) {
  const [tab, setTab] = useState<"sales" | "after-sales" | "operations" | "commissions" | "finance-sync" | "quality">(
    "sales",
  );
  return (
    <section className="stand-finance">
      <div className="finance-tabs" role="tablist">
        <button
          className={tab === "sales" ? "active" : ""}
          onClick={() => setTab("sales")}
        >
          Vendas e recebimentos
        </button>
        <button className={tab === "after-sales" ? "active" : ""} onClick={() => setTab("after-sales")}>Garantias e pós-venda</button>
        {(canViewCosts || canViewMargin) && (
          <button
            className={tab === "operations" ? "active" : ""}
            onClick={() => setTab("operations")}
          >
            Custos e margens
          </button>
        )}
        <button
          className={tab === "commissions" ? "active" : ""}
          onClick={() => setTab("commissions")}
        >
          Comissões
        </button>
        {canManageFinance && <button className={tab === "finance-sync" ? "active" : ""} onClick={() => setTab("finance-sync")}>Vorno Finance</button>}
        {canManageFinance && <button className={tab === "quality" ? "active" : ""} onClick={() => setTab("quality")}>Qualidade dos dados</button>}
      </div>
      {tab === "sales" ? (
        <SalesManagement
          organizationId={organizationId}
          currencyCode={currencyCode}
          writable={canManageSales}
          canValidate={canValidate}
          saleOpportunity={saleOpportunity}
          onSaleOpportunityConsumed={onSaleOpportunityConsumed}
        />
      ) : tab === "after-sales" ? (
        <AfterSalesCenter organizationId={organizationId} canManage={canManageSales || canManageFinance} />
      ) : tab === "quality" ? (
        <DataQualityCenter organizationId={organizationId}/>
      ) : tab === "finance-sync" ? (
        <FinanceMacroPanel organizationId={organizationId} currencyCode={currencyCode}/>
      ) : tab === "commissions" ? (
        <CommissionPanel
          organizationId={organizationId}
          canManage={canManageFinance}
        />
      ) : (
        <VehicleFinancePanel
          organizationId={organizationId}
          currencyCode={currencyCode}
          canManage={canManageFinance}
          canViewCosts={canViewCosts}
          canViewMargin={canViewMargin}
          canViewDocuments={canViewDocuments}
        />
      )}
    </section>
  );
}

type MacroIntegration={enabled:boolean;target_organization_id:string|null;target_environment:string;last_success_at:string|null;last_error:string|null};
type MacroBatch={id:string;period_start:string;period_end:string;status:string;income_cents:number;expense_cents:number;entry_count:number;attempt_count:number;last_error:string|null;remote_receipt_id:string|null;created_at:string};
const batchLabels:Record<string,string>={queued:"Em fila",delivering:"A enviar",delivered:"Entregue",failed:"A repetir",reconciled:"Reconciliado",cancelled:"Cancelado"};
function FinanceMacroPanel({organizationId,currencyCode}:{organizationId:string;currencyCode:string}){
 const today=new Date(),first=new Date(today.getFullYear(),today.getMonth(),1).toISOString().slice(0,10),last=today.toISOString().slice(0,10);
 const [integration,setIntegration]=useState<MacroIntegration|null>(null),[batches,setBatches]=useState<MacroBatch[]>([]),[notice,setNotice]=useState<string|null>(null),[busy,setBusy]=useState(false),[start,setStart]=useState(first),[end,setEnd]=useState(last);
 const load=async()=>{const [{data:i,error:ie},{data:b,error:be}]=await Promise.all([supabase.from("finance_macro_integrations").select("enabled,target_organization_id,target_environment,last_success_at,last_error").eq("organization_id",organizationId).maybeSingle(),supabase.from("finance_macro_batches").select("id,period_start,period_end,status,income_cents,expense_cents,entry_count,attempt_count,last_error,remote_receipt_id,created_at").eq("organization_id",organizationId).order("created_at",{ascending:false}).limit(12)]);if(ie||be)setNotice(ie?.message??be?.message??"Não foi possível carregar a integração.");setIntegration(i as MacroIntegration|null);setBatches((b??[]) as MacroBatch[])};
 useEffect(()=>{void load()},[organizationId]);
 const toggle=async()=>{if(!integration?.target_organization_id){setNotice("Este stand ainda não está associado à respetiva organização no Vorno Finance. Contacte a administração Vorno.");return}setBusy(true);const {error}=await supabase.rpc("configure_finance_macro_integration",{p_organization_id:organizationId,p_enabled:!integration.enabled,p_target_organization_id:integration.target_organization_id,p_target_environment:integration.target_environment});setNotice(error?.message??(!integration.enabled?"Integração ativada.":"Integração desligada. O Stand continua a funcionar normalmente."));if(!error)await load();setBusy(false)};
 const send=async(e:FormEvent)=>{e.preventDefault();setBusy(true);setNotice(null);const {data,error}=await supabase.functions.invoke("dispatch-finance-macro",{body:{organization_id:organizationId,period_start:start,period_end:end}});setNotice(error?String(data?.error??error.message):data?.duplicate?"Este período já estava entregue; nenhum valor foi duplicado.":"Agregados entregues ao Vorno Finance.");await load();setBusy(false)};
 return <section className="macro-sync"><header><div><p className="eyebrow">Ligação opcional</p><h2>Vorno Finance</h2><p>Envia apenas totais confirmados por período, filial e categoria. O detalhe das viaturas permanece no Stand.</p></div><button className={integration?.enabled?"stock-secondary":"stock-primary"} disabled={busy||!integration} onClick={toggle}>{integration?.enabled?"Desligar":"Ativar"}</button></header>{notice&&<button className="stock-notice" role="status" aria-live="polite" onClick={()=>setNotice(null)}>{notice} ×</button>}<div className="macro-state"><div><small>Estado</small><strong>{integration?.enabled?"Ligação ativa":"Ligação desligada"}</strong></div><div><small>Última entrega</small><strong>{integration?.last_success_at?new Date(integration.last_success_at).toLocaleString("pt-PT"):"Ainda sem entregas"}</strong></div></div>{integration?.enabled&&<form className="macro-send" onSubmit={send}><label><span>Desde</span><input type="date" value={start} onChange={e=>setStart(e.target.value)} required/></label><label><span>Até</span><input type="date" value={end} onChange={e=>setEnd(e.target.value)} required/></label><button className="stock-primary" disabled={busy}>Guardar</button></form>}<div className="macro-history"><h3>Histórico de envios</h3>{!batches.length?<p>Ainda não existem envios.</p>:batches.map(batch=><article key={batch.id}><div><strong>{batch.period_start} — {batch.period_end}</strong><span>{batch.entry_count} movimentos · {money(batch.income_cents,currencyCode)} receitas · {money(batch.expense_cents,currencyCode)} despesas</span>{batch.last_error&&<small>{batch.last_error}</small>}</div><b data-status={batch.status}>{batchLabels[batch.status]??batch.status}</b></article>)}</div></section>
}

function VehicleFinancePanel({
  organizationId,
  currencyCode,
  canManage,
  canViewCosts,
  canViewMargin,
  canViewDocuments,
}: {
  organizationId: string;
  currencyCode: string;
  canManage: boolean;
  canViewCosts: boolean;
  canViewMargin: boolean;
  canViewDocuments: boolean;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [summaries, setSummaries] = useState<Summary[]>([]),
    [entries, setEntries] = useState<Entry[]>([]),
    [documents, setDocuments] = useState<EntryDocument[]>([]),
    [selected, setSelected] = useState<string>(""),
    [creating, setCreating] = useState(false),
    [editing, setEditing] = useState<Entry | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [saving, setSaving] = useState(false);
  const load = async () => {
    const [v, s, e, d] = await Promise.all([
      supabase
        .from("vehicles")
        .select("id,stock_number,registration,make,model,currency_code")
        .eq("organization_id", organizationId)
        .eq("creation_status", "active")
        .order("stock_number"),
      canViewMargin
        ? supabase
            .from("vehicle_financial_summaries")
            .select(
              "vehicle_id,planned_expense_cents,realized_expense_cents,planned_income_cents,realized_income_cents,expected_margin_cents,realized_margin_cents,receivable_cents,currency_code",
            )
            .eq("organization_id", organizationId)
        : Promise.resolve({ data: [], error: null }),
      canViewCosts
        ? supabase
            .from("vehicle_financial_entries")
            .select(
              "id,vehicle_id,direction,category,state,amount_cents,currency_code,counterparty,branch_name,payment_method,effective_on,notes,created_at",
            )
            .eq("organization_id", organizationId)
            .is("voided_at", null)
            .order("created_at", { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      canViewDocuments
        ? supabase
            .from("financial_entry_documents")
            .select("id,entry_id,storage_path,original_name")
            .eq("organization_id", organizationId)
            .order("created_at", { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);
    setVehicles((v.data ?? []) as Vehicle[]);
    setSummaries((s.data ?? []) as Summary[]);
    setEntries((e.data ?? []) as Entry[]);
    setDocuments((d.data ?? []) as EntryDocument[]);
    if (v.error || s.error || e.error || d.error)
      setNotice(
        v.error?.message ?? s.error?.message ?? e.error?.message ?? d.error?.message ?? null,
      );
  };
  useEffect(() => void load(), [organizationId, canViewCosts, canViewMargin, canViewDocuments]);
  const totals = useMemo(
    () =>
      summaries.reduce(
        (a, s) => ({
          expense: a.expense + s.realized_expense_cents,
          income: a.income + s.realized_income_cents,
          margin: a.margin + s.realized_margin_cents,
          receivable: a.receivable + s.receivable_cents,
        }),
        { expense: 0, income: 0, margin: 0, receivable: 0 },
      ),
    [summaries],
  );
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const document = data.get("document") as File;
    const vehicleId = editing?.vehicle_id ?? String(data.get("vehicle_id"));
    setSaving(true);
    try {
      const request = editing
        ? await supabase.rpc("update_vehicle_financial_entry", {
            p_entry_id: editing.id,
            p_direction: data.get("direction"),
            p_category: data.get("category"),
            p_state: data.get("state"),
            p_amount_cents: Math.round(Number(data.get("amount")) * 100),
            p_counterparty: data.get("counterparty"),
            p_branch_name: data.get("branch_name"),
            p_payment_method: data.get("payment_method"),
            p_effective_on: data.get("effective_on") || null,
            p_notes: data.get("notes"),
            p_reason: data.get("reason"),
          })
        : await supabase.rpc("save_vehicle_financial_entry", {
          p_entry_id: null,
          p_vehicle_id: vehicleId,
          p_process_type: data.get("process_type"),
          p_tax_regime: data.get("tax_regime"),
          p_direction: data.get("direction"),
          p_category: data.get("category"),
          p_state: data.get("state"),
          p_amount_cents: Math.round(Number(data.get("amount")) * 100),
          p_counterparty: data.get("counterparty"),
          p_branch_name: data.get("branch_name"),
          p_payment_method: data.get("payment_method"),
          p_effective_on: data.get("effective_on") || null,
          p_notes: data.get("notes"),
          p_reason: null,
        });
      const { data: createdEntryId, error } = request;
      if (error) throw error;
      const entryId = editing?.id ?? createdEntryId;
      if (document?.size) {
        const safeName = document.name.replace(/[^a-zA-Z0-9._-]/g, "-");
        const path = `${organizationId}/${vehicleId}/${entryId}-${safeName}`;
        const upload = await supabase.storage
          .from("vehicle-financial-documents")
          .upload(path, document);
        if (upload.error) throw upload.error;
        const attached = await supabase.rpc("attach_financial_entry_document", {
          p_entry_id: entryId,
          p_storage_path: path,
          p_original_name: document.name,
          p_mime_type: document.type,
          p_size_bytes: document.size,
        });
        if (attached.error) throw attached.error;
      }
      setNotice("Lançamento guardado e margem recalculada.");
      setCreating(false);
      setEditing(null);
      await load();
    } catch (error) {
      setNotice(
        error instanceof Error
          ? error.message
          : "Não foi possível guardar o lançamento.",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: string) => {
    const reason = window.prompt(
      "Indique o motivo da anulação deste lançamento:",
    );
    if (!reason) return;
    const { error } = await supabase.rpc("void_vehicle_financial_entry", {
      p_entry_id: id,
      p_reason: reason,
    });
    setNotice(
      error?.message ?? "Lançamento anulado e preservado no histórico.",
    );
    if (!error) await load();
  };
  const openDocument = async (document: EntryDocument) => {
    const { data, error } = await supabase.storage
      .from("vehicle-financial-documents")
      .createSignedUrl(document.storage_path, 60);
    if (error || !data?.signedUrl) {
      setNotice(error?.message ?? "Não foi possível abrir o documento.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };
  const visibleEntries = selected
    ? entries.filter((entry) => entry.vehicle_id === selected)
    : entries;
  return (
    <div className="vehicle-finance">
      <header>
        <div>
          <p className="eyebrow">Financeiro operacional</p>
          <h2>Custos e margem por viatura</h2>
          <p>
            O detalhe fica no Stand. O Finance receberá futuramente apenas
            agregados confirmados.
          </p>
        </div>
        {canManage && (
          <button className="stock-primary" onClick={() => { setEditing(null); setCreating(true); }}>
            + Novo lançamento
          </button>
        )}
      </header>
      {notice && (
        <button className="stock-notice" role="status" aria-live="polite" onClick={() => setNotice(null)}>
          {notice} ×
        </button>
      )}
      {canViewMargin && (
        <div className="finance-kpis">
          <article>
            <small>Receitas recebidas</small>
            <strong>{money(totals.income, currencyCode)}</strong>
          </article>
          <article>
            <small>Custos pagos</small>
            <strong>{money(totals.expense, currencyCode)}</strong>
          </article>
          <article>
            <small>Margem realizada</small>
            <strong>{money(totals.margin, currencyCode)}</strong>
          </article>
          <article>
            <small>Por receber</small>
            <strong>{money(totals.receivable, currencyCode)}</strong>
          </article>
        </div>
      )}
      <label className="finance-vehicle-filter">
        <span>Viatura</span>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Todas as viaturas</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.registration || v.stock_number} · {v.make} {v.model}
            </option>
          ))}
        </select>
      </label>
      {canViewMargin && (
        <div className="margin-grid">
          {summaries
            .filter((s) => !selected || s.vehicle_id === selected)
            .map((summary) => {
              const v = vehicles.find((item) => item.id === summary.vehicle_id);
              return (
                <article key={summary.vehicle_id}>
                  <div>
                    <strong>{v?.registration || v?.stock_number}</strong>
                    <span>
                      {v?.make} {v?.model}
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>Custos realizados</dt>
                      <dd>
                        {money(
                          summary.realized_expense_cents,
                          summary.currency_code,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Receitas recebidas</dt>
                      <dd>
                        {money(
                          summary.realized_income_cents,
                          summary.currency_code,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Margem realizada</dt>
                      <dd>
                        {money(
                          summary.realized_margin_cents,
                          summary.currency_code,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Margem prevista</dt>
                      <dd>
                        {money(
                          summary.expected_margin_cents,
                          summary.currency_code,
                        )}
                      </dd>
                    </div>
                  </dl>
                </article>
              );
            })}
        </div>
      )}
      {canViewCosts && (
        <div className="financial-entry-list">
          {visibleEntries.map((entry) => {
            const v = vehicles.find((item) => item.id === entry.vehicle_id);
            return (
              <article key={entry.id}>
                <div>
                  <strong>
                    {categories[entry.category] ?? entry.category}
                  </strong>
                  <span>
                    {v?.registration || v?.stock_number} ·{" "}
                    {entry.counterparty || "Sem entidade"}
                  </span>
                </div>
                <div>
                  <b className={entry.direction}>
                    {entry.direction === "expense" ? "−" : "+"}{" "}
                    {money(entry.amount_cents, entry.currency_code)}
                  </b>
                  <small>{states[entry.state] ?? entry.state}</small>
                </div>
                {canViewDocuments && documents
                  .filter((document) => document.entry_id === entry.id)
                  .map((document) => (
                    <button key={document.id} className="entry-document" style={{ color: "var(--accent)" }} onClick={() => void openDocument(document)} title="Abrir documento numa ligação temporária e protegida">
                      Documento · {document.original_name}
                    </button>
                  ))}
                {canManage && (
                  <div className="entry-actions"><button style={{ color: "var(--accent)" }} onClick={() => { setEditing(entry); setCreating(true); }}>Editar</button><button onClick={() => void remove(entry.id)}>Anular</button></div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {creating && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog finance-entry-dialog">
            <header>
              <div>
                <p className="eyebrow">{editing ? "Alterar movimento" : "Novo movimento"}</p>
                <h2>{editing ? "Editar lançamento" : "Lançamento da viatura"}</h2>
                <p>
                  A seleção fiscal é registada para auditoria, mas a
                  responsabilidade pertence ao stand.
                </p>
              </div>
              <button
                className="stock-close"
                onClick={() => { setCreating(false); setEditing(null); }}
              >
                ×
              </button>
            </header>
            <form onSubmit={submit}>
              <div className="stock-form-grid">
                <label className="wide">
                  <span>Viatura *</span>
                  <select name="vehicle_id" required defaultValue={editing?.vehicle_id ?? ""} disabled={Boolean(editing)}>
                    <option value="">Selecione</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.registration || v.stock_number} · {v.make} {v.model}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Processo *</span>
                  <small className="field-help">Indica se este movimento pertence à compra ou à venda da viatura.</small>
                  <select name="process_type" disabled={Boolean(editing)}>
                    <option value="acquisition">Aquisição</option>
                    <option value="sale">Venda</option>
                  </select>
                </label>
                <label>
                  <span>Regime fiscal *</span>
                  <small className="field-help">Define como o IVA deste negócio será estimado. Confirme o enquadramento com a contabilidade.</small>
                  <select name="tax_regime" disabled={Boolean(editing)}>
                    <option value="margin_scheme">Regime da margem</option>
                    <option value="standard_vat">IVA normal</option>
                    <option value="vat_exempt">Isento de IVA</option>
                    <option value="intra_community">Intracomunitário</option>
                    <option value="import">Importação</option>
                    <option value="other">Outro</option>
                  </select>
                </label>
                <label>
                  <span>Natureza *</span>
                  <small className="field-help">Custo reduz a margem da viatura; receita aumenta o resultado do negócio.</small>
                  <select name="direction" defaultValue={editing?.direction ?? "expense"}>
                    <option value="expense">Custo</option>
                    <option value="income">Receita</option>
                  </select>
                </label>
                <label>
                  <span>Categoria *</span>
                  <select name="category" defaultValue={editing?.category ?? "acquisition"}>
                    {Object.entries(categories).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Estado *</span>
                  <small className="field-help">Previsto entra na projeção; confirmado entra no resultado realizado.</small>
                  <select name="state" defaultValue={editing?.state ?? "planned"}>
                    {Object.entries(states).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Valor ({currencyCode}) *</span>
                  <CurrencyInput
                    currencyCode={currencyCode}
                    name="amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    defaultValue={editing ? editing.amount_cents / 100 : undefined}
                  />
                </label>
                <label>
                  <span>Entidade</span>
                  <small className="field-help">Pessoa ou empresa a quem o valor foi pago ou de quem foi recebido.</small>
                  <input name="counterparty" defaultValue={editing?.counterparty ?? ""} />
                </label>
                <label>
                  <span>Filial</span>
                  <input name="branch_name" defaultValue={editing?.branch_name ?? ""} />
                </label>
                <label>
                  <span>Meio de pagamento</span>
                  <input name="payment_method" defaultValue={editing?.payment_method ?? ""} />
                </label>
                <label>
                  <span>Data efetiva</span>
                  <small className="field-help">Data em que o custo ou a receita ocorreu para efeitos do histórico financeiro.</small>
                  <input name="effective_on" type="date" defaultValue={editing?.effective_on ?? ""} />
                </label>
                <label>
                  <span>Documento</span>
                  <small className="field-help">Fatura, recibo ou comprovativo que sustenta este movimento.</small>
                  <input
                    name="document"
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                  />
                </label>
                <label className="wide">
                  <span>Notas</span>
                  <textarea name="notes" defaultValue={editing?.notes ?? ""} />
                </label>
                {editing && <label className="wide"><span>Motivo da alteração *</span><textarea name="reason" minLength={5} required placeholder="Explique a alteração para o histórico." /></label>}
              </div>
              <p className="fiscal-responsibility">
                Ao guardar, confirma que o stand é responsável pelo
                enquadramento fiscal escolhido.
              </p>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => { setCreating(false); setEditing(null); }}
                >
                  Cancelar
                </button>
                <button className="stock-primary" disabled={saving}>
                  {saving ? "A guardar…" : "Guardar"}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

function CommissionPanel({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const [commissions, setCommissions] = useState<Commission[]>([]),
    [sales, setSales] = useState<Sale[]>([]),
    [notice, setNotice] = useState<string | null>(null),
    [editing, setEditing] = useState<Sale | null>(null);
  const load = async () => {
    const [c, s] = await Promise.all([
      supabase
        .from("vehicle_sale_commissions")
        .select("id,sale_id,amount_cents,currency_code,status,seller_user_id")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false }),
      supabase
        .from("vehicle_sales")
        .select("id,vehicle_id,customer_name,sale_price_cents,currency_code")
        .eq("organization_id", organizationId)
        .neq("status", "cancelled"),
    ]);
    setCommissions((c.data ?? []) as Commission[]);
    setSales((s.data ?? []) as Sale[]);
    if (c.error || s.error)
      setNotice(c.error?.message ?? s.error?.message ?? null);
  };
  useEffect(() => void load(), [organizationId]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const data = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("save_sale_commission", {
      p_sale_id: editing.id,
      p_amount_cents: Math.round(Number(data.get("amount")) * 100),
      p_status: data.get("status"),
      p_notes: data.get("notes"),
    });
    setNotice(error?.message ?? "Comissão atualizada.");
    if (!error) {
      setEditing(null);
      await load();
    }
  };
  return (
    <div className="commission-panel">
      <header>
        <div>
          <p className="eyebrow">Comissões</p>
          <h2>Comissões das vendas</h2>
          <p>
            O comercial vê apenas as próprias comissões. Estados: prevista,
            aprovada e paga.
          </p>
        </div>
      </header>
      {notice && (
        <button className="stock-notice" role="status" aria-live="polite" onClick={() => setNotice(null)}>
          {notice} ×
        </button>
      )}
      <div className="commission-list">
        {sales.map((sale) => {
          const commission = commissions.find(
            (item) => item.sale_id === sale.id,
          );
          return (
            <article key={sale.id}>
              <div>
                <strong>{sale.customer_name}</strong>
                <span>
                  Venda: {money(sale.sale_price_cents, sale.currency_code)}
                </span>
              </div>
              <div>
                <b>
                  {commission
                    ? money(commission.amount_cents, commission.currency_code)
                    : "Sem comissão definida"}
                </b>
                <small>
                  {commission ? commissionStates[commission.status] : "—"}
                </small>
              </div>
              {canManage && (
                <button
                  className="stock-secondary"
                  onClick={() => setEditing(sale)}
                >
                  Definir
                </button>
              )}
            </article>
          );
        })}
      </div>
      {editing && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog commission-dialog">
            <header>
              <div>
                <p className="eyebrow">Comissão</p>
                <h2>{editing.customer_name}</h2>
              </div>
              <button className="stock-close" onClick={() => setEditing(null)}>
                ×
              </button>
            </header>
            <form onSubmit={submit}>
              {(() => { const current=commissions.find(item=>item.sale_id===editing.id); return <>
              <label>
                <span>Valor *</span>
                <CurrencyInput
                  currencyCode={editing.currency_code}
                  name="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  defaultValue={current ? current.amount_cents / 100 : undefined}
                  readOnly={Boolean(current && current.status !== "expected")}
                />
              </label>
              <label>
                <span>Estado *</span>
                <select name="status" defaultValue={current?.status ?? "expected"}>
                  {(!current || current.status==="expected")&&<option value="expected">Prevista</option>}
                  {current?.status==="expected"&&<option value="approved">Aprovar</option>}
                  {current?.status==="approved"&&<option value="approved">Aprovada</option>}
                  {current?.status==="approved"&&<option value="paid">Marcar como paga</option>}
                  {current?.status==="paid"&&<option value="paid">Paga</option>}
                </select>
                <small>A comissão segue obrigatoriamente Prevista → Aprovada → Paga.</small>
              </label>
              <label>
                <span>Notas</span>
                <textarea name="notes" />
              </label>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => setEditing(null)}
                >
                  Cancelar
                </button>
                <button className="stock-primary">Guardar</button>
              </footer>
              </>})()}
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
